import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Field, getIn } from 'formik'
import classNames from 'classnames'

import { FormGroup } from '@peracto/peracto-ui'
import config from './config'

const getCharacterCount = html => {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return doc.body.textContent.length || 0
}

// Adds the data-testid attributes the Cypress tests use to find the CKEditor instances. A component
// of its own so these effects are not called conditionally inside the Field render callback.
const CKEditorTestIds = ({ name }) => {
    useEffect(() => {
        const domEditableElement = document.querySelector('.ck-editor__editable')
        const editorInstance = domEditableElement?.ckeditorInstance

        if (editorInstance) {
            editorInstance.editing.view.change(writer => {
                const viewEditableRoot = editorInstance.editing.view.domConverter.mapDomToView(
                    domEditableElement
                )
                writer.setAttribute('data-testid', `richtext-${name}`, viewEditableRoot)
            })
        }
    }, [name])

    useEffect(() => {
        const temp = document.getElementsByClassName('ck-editor__main')
        for (let index = 0; index < temp.length; index++) {
            const t1 = document.getElementsByClassName('ck-editor__main')[index]
            let t2 = document.createAttribute('data-testid')
            t2.value = `richtext-attribute-${index}`
            t1.setAttributeNode(t2)
        }
    })

    return null
}

const RichText = ({ name, label, help, className, showWhen, hideCharacterCount, ...props }) => {
    const editorRef = useRef()
    const [editorLoaded, setEditorLoaded] = useState(false)
    const { CKEditor, ClassicEditor } = editorRef.current || {}

    useEffect(() => {
        const { ClassicEditor } = require('@peracto/ckeditor')

        const { CKEditor: CKEditorReact } = require('@ckeditor/ckeditor5-react')
        editorRef.current = {
            CKEditor: CKEditorReact,
            ClassicEditor,
        }
        setEditorLoaded(true)
    }, [])

    return (
        editorLoaded && (
            <Field name={name}>
                {({ field, form }) => {
                    let error = getIn(form.errors, name)

                    if (error) {
                        const errorFieldName = label ? label : 'This'
                        error = error.replace(field.name, errorFieldName)
                    }

                    if (showWhen) {
                        const showField = form.values.hasOwnProperty(showWhen)
                            ? form.values[showWhen]
                            : false

                        if (!showField) {
                            return null
                        }
                    }

                    props.className = classNames('form-control', className)

                    if (help) {
                        props['aria-describedby'] = `${name}Help`
                    }

                    return (
                        <FormGroup
                            {...{ name, label, help }}
                            charactercount={
                                hideCharacterCount ? false : getCharacterCount(field.value || '')
                            }
                            error={error}
                        >
                            <CKEditor
                                editor={ClassicEditor}
                                config={{
                                    placeholder: props.placeholder
                                        ? props.placeholder
                                        : 'Enter your text here...',
                                    ...config,
                                }}
                                data={field.value || ''}
                                onChange={(event, editor) => {
                                    form.setFieldValue(name, editor.getData())
                                }}
                                disabled={form.isSubmitting || props.disabled}
                            />
                            <CKEditorTestIds name={name} />
                        </FormGroup>
                    )
                }}
            </Field>
        )
    )
}

RichText.displayName = 'RichText'

RichText.propTypes = {
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    help: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    placeholder: PropTypes.string,
    className: PropTypes.string,
}

export default RichText
